/**
 * Cart UI Sync (Muokkis theme)
 * -----------------------------------------------------------------------------
 * Purpose: Ensure header cart count updates after any cart mutation,
 * and inject a checkout-visible property for personalized products.
 *
 * When a Teeinblue-customized product is added to cart, this script adds
 * "✓ Design: Personoitu & tallennettu" as a line item property. This shows
 * on the Shopify checkout page, reassuring customers their customization is saved
 * (since Shopify checkout cannot display custom images for non-Plus stores).
 */
(function() {
  'use strict';

  // Save original references for our own use
  var OriginalXHR = window.XMLHttpRequest;
  var originalFetch = window.fetch;

  // Track last known cart count to avoid unnecessary DOM updates
  var lastKnownCartCount = null;

  // Checkout property text (Finnish for Muokkis)
  var DESIGN_PROPERTY_KEY = '✓ Design';
  var DESIGN_PROPERTY_VALUE = 'Personoitu & tallennettu';

  // ---------------------------------------------------------------------------
  // Utilities
  // ---------------------------------------------------------------------------

  function isCartMutationUrl(url) {
    if (!url || typeof url !== 'string') return false;
    return (
      url.indexOf('/cart/add') !== -1 ||
      url.indexOf('/cart/change') !== -1 ||
      url.indexOf('/cart/update') !== -1 ||
      url.indexOf('/cart/clear') !== -1
    );
  }

  /**
   * Check if the cart add request is for a Teeinblue customized product.
   * We only inject the "✓ Design" property for these products to avoid
   * polluting regular product line items.
   */
  function isTeeinblueProduct(data) {
    if (!data) return false;
    
    // Check for _customization_id in properties (indicates Teeinblue product)
    if (typeof data === 'object' && data.properties) {
      return '_customization_id' in data.properties || 
             '_customization_image' in data.properties;
    }
    
    // Check JSON string
    if (typeof data === 'string') {
      return data.indexOf('_customization_id') !== -1 ||
             data.indexOf('_customization_image') !== -1;
    }
    
    // Check FormData
    if (data instanceof FormData) {
      var hasCustomization = false;
      if (typeof data.get === 'function') {
        hasCustomization = data.get('properties[_customization_id]') !== null ||
                          data.get('properties[_customization_image]') !== null;
      }
      return hasCustomization;
    }
    
    return false;
  }

  /**
   * Updates all cart count UI elements.
   */
  function applyCartCount(itemCount) {
    if (itemCount === lastKnownCartCount) return;
    lastKnownCartCount = itemCount;

    // Update elements with class or id for cart count
    var cartCountElements = document.querySelectorAll('[id="CartCount"], .cart-count, .m-cart-count');
    for (var i = 0; i < cartCountElements.length; i++) {
      cartCountElements[i].textContent = itemCount;
    }
  }

  /**
   * Fetch /cart.js and update cart counters.
   */
  function syncFromCartJson() {
    var countRequest = new OriginalXHR();
    countRequest.open('GET', '/cart.js?t=' + Date.now(), true);
    countRequest.setRequestHeader('Accept', 'application/json');

    countRequest.onload = function() {
      if (countRequest.status >= 200 && countRequest.status < 400) {
        try {
          var cart = JSON.parse(countRequest.responseText);
          if (cart && typeof cart.item_count === 'number') {
            applyCartCount(cart.item_count);
          }
        } catch (e) {
          console.error('[Cart Sync] Error parsing /cart.js:', e);
        }
      }
    };
    countRequest.send();
  }

  // ---------------------------------------------------------------------------
  // Throttled sync scheduler
  // ---------------------------------------------------------------------------
  var syncScheduled = false;

  function scheduleSync() {
    if (syncScheduled) return;
    syncScheduled = true;

    setTimeout(function() {
      syncScheduled = false;
      syncFromCartJson();
    }, 500);
  }

  // ---------------------------------------------------------------------------
  // Hook 1: Native fetch() API
  // ---------------------------------------------------------------------------
  if (typeof originalFetch === 'function') {
    window.fetch = function(input, init) {
      var url = '';
      if (typeof input === 'string') {
        url = input;
      } else if (input instanceof URL) {
        url = input.href;
      } else if (input && typeof input.url === 'string') {
        url = input.url;
      }

      // For /cart/add with Teeinblue products, inject checkout property
      var modifiedInit = init;
      if (url.indexOf('/cart/add') !== -1 && init && init.body) {
        try {
          // Handle JSON body
          if (typeof init.body === 'string') {
            try {
              var bodyJson = JSON.parse(init.body);
              if (isTeeinblueProduct(bodyJson)) {
                if (!bodyJson.properties) bodyJson.properties = {};
                bodyJson.properties[DESIGN_PROPERTY_KEY] = DESIGN_PROPERTY_VALUE;
                modifiedInit = Object.assign({}, init, { body: JSON.stringify(bodyJson) });
              }
            } catch (e) {
              // Not valid JSON, skip
            }
          }
          // Handle FormData body
          else if (init.body instanceof FormData && isTeeinblueProduct(init.body)) {
            var newFormData = new FormData();
            for (var pair of init.body.entries()) {
              newFormData.append(pair[0], pair[1]);
            }
            newFormData.append('properties[' + DESIGN_PROPERTY_KEY + ']', DESIGN_PROPERTY_VALUE);
            modifiedInit = Object.assign({}, init, { body: newFormData });
          }
        } catch (e) {
          console.warn('[Cart Sync] Could not inject checkout property:', e);
        }
      }

      var fetchPromise = originalFetch.call(this, input, modifiedInit);

      // After cart mutations, sync UI
      if (isCartMutationUrl(url)) {
        fetchPromise.then(function(response) {
          if (response.ok) {
            scheduleSync();
          }
          return response;
        }).catch(function() {});
      }

      return fetchPromise;
    };
  }

  // ---------------------------------------------------------------------------
  // Hook 2: jQuery AJAX
  // ---------------------------------------------------------------------------
  function setupJQueryHook() {
    if (typeof window.jQuery === 'undefined') return;

    var $ = window.jQuery;
    var originalAjax = $.ajax;

    if ($.ajax._cartSyncHooked) return;

    $.ajax = function(urlOrSettings, settings) {
      var opts = typeof urlOrSettings === 'string'
        ? $.extend({}, settings, { url: urlOrSettings })
        : $.extend({}, urlOrSettings);

      var url = opts.url || '';

      // For /cart/add with Teeinblue products, inject checkout property
      if (url.indexOf('/cart/add') !== -1 && opts.data && isTeeinblueProduct(opts.data)) {
        try {
          if (typeof opts.data === 'object' && !(opts.data instanceof FormData)) {
            if (!opts.data.properties) opts.data.properties = {};
            opts.data.properties[DESIGN_PROPERTY_KEY] = DESIGN_PROPERTY_VALUE;
          }
          else if (typeof opts.data === 'string') {
            try {
              var bodyJson = JSON.parse(opts.data);
              if (!bodyJson.properties) bodyJson.properties = {};
              bodyJson.properties[DESIGN_PROPERTY_KEY] = DESIGN_PROPERTY_VALUE;
              opts.data = JSON.stringify(bodyJson);
            } catch (e) {
              opts.data += '&properties[' + DESIGN_PROPERTY_KEY + ']=' + encodeURIComponent(DESIGN_PROPERTY_VALUE);
            }
          }
        } catch (e) {
          console.warn('[Cart Sync] Could not inject checkout property (jQuery):', e);
        }
      }

      var jqXHR = originalAjax.call(this, opts);

      if (isCartMutationUrl(url)) {
        jqXHR.done(function() {
          scheduleSync();
        });
      }

      return jqXHR;
    };

    $.ajax._cartSyncHooked = true;
  }

  setupJQueryHook();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupJQueryHook);
  }
  setTimeout(setupJQueryHook, 100);

  // ---------------------------------------------------------------------------
  // Hook 3: XMLHttpRequest
  // ---------------------------------------------------------------------------
  var XHRSend = XMLHttpRequest.prototype.send;
  var XHROpen = XMLHttpRequest.prototype.open;

  XMLHttpRequest.prototype.open = function(method, url) {
    this._cartSyncUrl = url;
    this._cartSyncMethod = method;
    XHROpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function(data) {
    var xhr = this;
    var url = xhr._cartSyncUrl || '';
    var modifiedData = data;

    // For /cart/add with Teeinblue products, inject checkout property
    if (url.indexOf('/cart/add') !== -1 && data && isTeeinblueProduct(data)) {
      try {
        if (typeof data === 'string') {
          try {
            var bodyJson = JSON.parse(data);
            if (!bodyJson.properties) bodyJson.properties = {};
            bodyJson.properties[DESIGN_PROPERTY_KEY] = DESIGN_PROPERTY_VALUE;
            modifiedData = JSON.stringify(bodyJson);
          } catch (e) {
            modifiedData = data + '&properties[' + DESIGN_PROPERTY_KEY + ']=' + encodeURIComponent(DESIGN_PROPERTY_VALUE);
          }
        }
        else if (data instanceof FormData) {
          var newFormData = new FormData();
          for (var pair of data.entries()) {
            newFormData.append(pair[0], pair[1]);
          }
          newFormData.append('properties[' + DESIGN_PROPERTY_KEY + ']', DESIGN_PROPERTY_VALUE);
          modifiedData = newFormData;
        }
      } catch (e) {
        console.warn('[Cart Sync] Could not inject checkout property (XHR):', e);
      }
    }

    XHRSend.call(this, modifiedData);

    if (isCartMutationUrl(url)) {
      xhr.addEventListener('load', function() {
        if (xhr.status >= 200 && xhr.status < 400) {
          scheduleSync();
        }
      });
    }
  };

  // ---------------------------------------------------------------------------
  // Hook 4: Custom events
  // ---------------------------------------------------------------------------
  var cartEvents = ['cart:updated', 'cart:refresh', 'ajaxCart.afterCartLoad'];
  cartEvents.forEach(function(eventName) {
    document.addEventListener(eventName, function() {
      scheduleSync();
    });
  });

  // ---------------------------------------------------------------------------
  // Global API
  // ---------------------------------------------------------------------------
  window.MuokkisCartSync = window.MuokkisCartSync || {};
  window.MuokkisCartSync.sync = syncFromCartJson;
  window.MuokkisCartSync.scheduleSync = scheduleSync;

})();
